import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { Box } from 'components/Layout'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT } from 'Theme'
import { TRADE_PERK_CATEGORIES, TRADE_TIERS } from '../tradePerksData'

const { TRADE_PAGE } = COLOR_INTENT.WIDGETS

const isBigJump = (tierIndex: number): boolean =>
  tierIndex === 2 || tierIndex === 4

const Card = styled(Box)`
  background: ${TRADE_PAGE.WHITE};
  border: 1px solid ${TRADE_PAGE.BORDER_CARD};
  border-radius: ${rem(14)};
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`

const TableScroll = styled(Box)`
  min-width: ${rem(900)};
`

const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  col.benefit {
    width: ${rem(200)};
  }

  col.tier {
    width: ${rem(104)};
  }

  th {
    font-weight: ${FONT_WEIGHT.NORMAL};
    text-align: left;
  }

  thead tr {
    background: ${TRADE_PAGE.DARK_BG};
    color: ${TRADE_PAGE.TABLE_HEADER_TEXT};
  }

  thead th {
    padding: ${rem(18)};
    font-family: ${FONT_STACK.SANS};
    font-size: ${rem(12)};
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  thead th:nth-of-type(2) {
    text-align: center;
  }

  thead th:nth-of-type(n + 3) {
    padding: ${rem(18)} ${rem(6)};
    text-align: center;
  }

  thead th.highlight {
    background: rgba(228, 196, 107, 0.14);
  }

  .tierName {
    display: block;
    font-family: ${FONT_STACK.SERIF};
    font-size: ${rem(17)};
    font-weight: ${FONT_WEIGHT.NORMAL};
    line-height: 1;
    text-transform: none;
    letter-spacing: normal;
  }

  .tierRange {
    display: block;
    font-size: ${rem(10.5)};
    font-weight: ${FONT_WEIGHT.NORMAL};
    text-transform: none;
    letter-spacing: normal;
    opacity: 0.72;
    margin-top: ${rem(4)};
  }

  .categoryRow th {
    background: ${TRADE_PAGE.MIST_BG};
    border-top: 1px solid ${TRADE_PAGE.BORDER_CARD};
    padding: ${rem(10)} ${rem(18)};
    font-family: ${FONT_STACK.SANS};
    font-size: ${rem(11.5)};
    font-weight: ${FONT_WEIGHT.BOLD};
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${TRADE_PAGE.ACCENT};
  }

  tbody tr:not(.categoryRow) {
    border-top: 1px solid ${TRADE_PAGE.BORDER_ROW};
  }

  tbody th[scope='row'] {
    padding: ${rem(15)} ${rem(18)};
    font-family: ${FONT_STACK.SANS};
    font-size: ${rem(13.5)};
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
    color: ${TRADE_PAGE.TEXT_DARK};
  }

  .desc {
    padding: ${rem(15)} ${rem(18)};
    font-family: ${FONT_STACK.SANS};
    font-size: ${rem(12.5)};
    color: ${TRADE_PAGE.TEXT_SOFT};
    line-height: 1.45;
    text-align: center;
  }

  tbody td:not(.desc) {
    padding: ${rem(15)} ${rem(6)};
    text-align: center;
    border-left: 1px solid ${TRADE_PAGE.BORDER_CELL};
  }

  tbody td.highlight {
    background: rgba(228, 196, 107, 0.1);
  }

  .badge {
    font-family: ${FONT_STACK.SANS};
    font-size: ${rem(11)};
    font-weight: ${FONT_WEIGHT.MEDIUM};
    color: ${TRADE_PAGE.HEADING};
    line-height: 1.35;
  }
`

const Dot = styled.span`
  display: inline-block;
  width: ${rem(9)};
  height: ${rem(9)};
  border-radius: 50%;
  background: ${TRADE_PAGE.ACCENT};
`

export const PerksTable: React.FC = () => (
  <Card>
    <TableScroll>
      <StyledTable>
        <colgroup>
          <col className="benefit" />
          <col />
          {TRADE_TIERS.map((tier) => (
            <col className="tier" key={tier.name} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Benefit</th>
            <th scope="col">What you get</th>
            {TRADE_TIERS.map((tier, index) => (
              <th
                scope="col"
                key={tier.name}
                className={isBigJump(index) ? 'highlight' : undefined}
              >
                <span className="tierName">{tier.name}</span>
                <span className="tierRange">{tier.range}</span>
              </th>
            ))}
          </tr>
        </thead>
        {TRADE_PERK_CATEGORIES.map((category) => (
          <tbody key={category.key}>
            <tr className="categoryRow">
              <th scope="rowgroup" colSpan={7}>
                {category.tableLabel}
              </th>
            </tr>
            {category.perks.map((perk) => (
              <tr key={perk.name}>
                <th scope="row">{perk.name}</th>
                <td className="desc">{perk.desc}</td>
                {TRADE_TIERS.map((tier, tierIndex) => (
                  <td
                    key={tier.name}
                    className={isBigJump(tierIndex) ? 'highlight' : undefined}
                  >
                    {tierIndex >= perk.fromTier &&
                      (perk.badge ? (
                        <span className="badge">{perk.badge}</span>
                      ) : (
                        <Dot />
                      ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </StyledTable>
    </TableScroll>
  </Card>
)
